import type { LocationQuery, RouteLocationNormalized } from 'vue-router';

import SearchUtils from '@/utils/search-utils';

// UI2 hands the session over in the query string, and the router's first navigation starts
// before the bootstrap plugin scrubs it from the address bar.
export const HANDOFF_CREDENTIAL_QUERY_KEYS: readonly string[] = [
  'token',
  'refreshToken',
  'refreshTokenExpiresAt',
];

const REDACTED_VALUE = '_redacted_';

export interface TrackedPageVisit {
  name: 'Page View';
  path: string;
  search: string;
  title: string;
  url: string;
}

export function redactHandoffCredentials(query: LocationQuery): LocationQuery {
  return Object.fromEntries(
    Object.entries(query).map(([key, value]) => [
      key,
      HANDOFF_CREDENTIAL_QUERY_KEYS.includes(key) ? REDACTED_VALUE : value,
    ]),
  );
}

// Repeated keys stay repeated, as the address bar has them, rather than SearchUtils' bracket form.
function toSearchString(query: LocationQuery): string {
  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    const values = Array.isArray(value) ? value : [value];

    values.forEach((entry) => params.append(key, entry ?? ''));
  });

  return params.toString();
}

export function buildTrackedPageVisit(
  to: Pick<RouteLocationNormalized, 'path' | 'name' | 'query' | 'hash'>,
  origin: string,
): TrackedPageVisit {
  const query = redactHandoffCredentials(to.query);
  const urlSearch = toSearchString(query);

  return {
    name: 'Page View',
    path: to.path,
    search: SearchUtils.convertSearchObjectToSearchUrl(query),
    title: to.name?.toString() || '',
    url: `${origin}${to.path}${urlSearch ? `?${urlSearch}` : ''}${to.hash}`,
  };
}
